import { hasButtonEditorContent, hasLightEditorContent } from "./C1_editorAvailability";
export { hasButtonEditorContent, hasLightEditorContent } from "./C1_editorAvailability";
import { buttonUiTemplate } from "../frontViews/buttonUiTemplate";
import { C1_ELO_PLUG_TYPE_LABELS, C1_ELO_VOLTAGE_LABELS, C1_ELO_SENSOR_TYPE_LABELS } from "../C1_uiLabels";
import { itx } from "../../../src/translationItx/itx";
// import { C1_EloEditorPin } from "./C1_EloEditorPin";
import { C1_LightEditorFields } from "./C1_LightEditorFields";
import { C1_SocketTypeSelect } from "./C1_SocketTypeSelect";
import { freezeEditorSizeAfterLayout } from "./C1_freezeEditorSize";
import { lazy, Suspense, useEffect, useLayoutEffect, useRef } from "react";
import { newId } from "../../../src/models/BaseObject";
import type BaseModel from "../../../src/models/BaseModel";
import { C1_EventKey } from "../C1_EventKey";
import C1_EloItem from "../model/C1_EloItem";
import type { C1_EloGroup } from "../model/C1_EloGroup";
import { commitOwnerEloTree, eloRootAndOwner } from "../model/C1_eloOwnerEditing";
import { socketGroupForSelection } from "./C1_socketGroupForSelection";
import { C1_Button } from "../model/C1_Button";
import { C1_Consumer } from "../model/C1_Consumer";
import { isEloItemDefaultEnabled, reconcileEloEquipmentInstance } from "../model/C1_ensureEloEquipmentDefaults";
import type { C1_State } from "../types/C1_ModelTypes";
import type { C1_EquipmentStandard } from "../types/C1_ModelTypes";
import type {
  C1_EloItem_Definition,
  C1_EloItem_Detail_Options,
  C1_EloItem_Details,
  C1_EloItem_Data,
} from "../types/C1_EloTypes";
import {
  C1_EloItem_Type,
  createDefaultButtonProperties,
} from "../types/C1_EloTypes";
import { useOnChangeWatcher } from "../../../src/models/useOnChangeWatcher";
import C1_eloItemsRegistry from "../data/C1_eloItemsRegistry";
import { C1_Dialog } from "../components/C1_Dialog";
import {
  getButtonProductDefinition,
  getButtonProductIds,
  getLightProductIdsForUsage,
} from "../data/C1_ProductDefinitions";
import c1_state from "../model/C1_State";
import { useModel } from "../model/C1_useModel";
import { removeRepeatedItem, repeatItemCount, repeatItemLimit, updateEloItemData } from "../model/C1_eloTreeEditing";
import { ApplicationMode } from "../../../src/models/SettingsEnums";
import type Button from "../../../src/models/Button";
import { C1_ButtonEditorView, selectedButtonAssignmentChannel } from "./C1_ButtonEditorView";
import { C1_ButtonChannelList } from "./C1_ButtonChannelList";
import { Settings } from "lucide-react";
import { C1_FurnitureEditor } from "./C1_FurnitureEditor";
import { C1_EloNameField } from "./C1_EloNameField";
import { DisplayNameUsage } from "../../../src/models/BaseObject";
import { getC1EloEquipmentIcon } from "../furniture/C1_EloEquipmentIcon";

const TemplateSelection = lazy(() => import("../../../src/components/TemplateSelection").then(module => ({ default: module.TemplateSelection })));
const ConsumerTypeSelection = lazy(() => import("../../../src/components/ConsumerPropertiesView/ConsumerTypeSelection"));

const detailDefault = <T,>(
  option: { default?: Partial<Record<`S${C1_EquipmentStandard}`, T>> } | undefined,
  equipmentStandard: C1_EquipmentStandard,
) => option?.default?.[`S${equipmentStandard}`];

function initialDetails(
  definition: C1_EloItem_Definition,
  equipmentStandard: C1_EquipmentStandard,
): C1_EloItem_Details | undefined {
  const options = definition.details;
  if (!options) return definition.defaultDetails ? { ...definition.defaultDetails } : undefined;

  return {
    itemCount: options.defaultItemCount ?? (Array.isArray(options.repeatCount) ? undefined : options.repeatCount),
    numberOfButtonChannels: options.numberOfButtonChannels,
    buttonType: detailDefault(options.buttonType, equipmentStandard),
    channelType: detailDefault(options.channelType, equipmentStandard),
    lightProductId: detailDefault(options.lightProductId, equipmentStandard),
    plugType: detailDefault(options.plugType, equipmentStandard),
    voltage: detailDefault(options.voltage, equipmentStandard),
    sensorTypes: detailDefault(options.sensorTypes, equipmentStandard),
    ...definition.defaultDetails,
  };
}

function expandedItemDefinitions(
  definition: C1_EloItem_Definition,
  details?: C1_EloItem_Details,
): C1_EloItem_Definition[] {
  const items = definition.items ?? [];
  if (definition.type !== C1_EloItem_Type.Group && definition.type !== C1_EloItem_Type.PlugGroup) return items;
  if (definition.details?.repeatCount === undefined) return items;

  const itemCount =
    details?.itemCount ??
    definition.details.defaultItemCount ??
    (Array.isArray(definition.details.repeatCount) ? undefined : definition.details.repeatCount) ??
    1;
  return Array.from({ length: Math.max(0, Math.floor(itemCount)) }, (_, index) => index + 1).flatMap((repeatIndex) =>
    items.map((item) => {
      const repeatDefault = definition.repeatDefaults?.[repeatIndex - 1];
      const repeatedDescription = (language: "de" | "en") =>
        [item.repeatDescription?.[language] ?? item.description[language], `#${repeatIndex}`].filter(Boolean).join(" ");
      return {
        ...item,
        id: `${item.id}-${repeatIndex}`,
        repeatIndex,
        defaultDetails: { ...item.defaultDetails, ...repeatDefault?.details },
        description: {
          en: repeatedDescription("en"),
          de: repeatedDescription("de"),
        },
      };
    }),
  );
}

function createInstance(definition: C1_EloItem_Definition, equipmentStandard: C1_EquipmentStandard): C1_EloItem_Data {
  const details = initialDetails(definition, equipmentStandard);
  const buttonProduct = getButtonProductDefinition(details?.buttonType);
  const buttonTemplate = buttonProduct?.uiTemplate;
  const buttonDefaults =
    buttonTemplate === "simple" || buttonTemplate === "touch"
      ? c1_state.settings.buttonDefaults[buttonTemplate]
      : undefined;
  return {
    id: newId(),
    definitionId: definition.id,
    name: definition.description.de,
    details,
    properties:
      definition.type === C1_EloItem_Type.Button
        ? createDefaultButtonProperties(buttonProduct, buttonDefaults)
        : undefined,
    items: expandedItemDefinitions(definition, details)
      .filter((item) => isEloItemDefaultEnabled(item, equipmentStandard))
      .map((item) => createInstance(item, equipmentStandard)),
  };
}

function createDefaultInstances(definitions: C1_EloItem_Definition[], equipmentStandard: C1_EquipmentStandard) {
  return definitions
    .filter((definition) => isEloItemDefaultEnabled(definition, equipmentStandard))
    .map((definition) => createInstance(definition, equipmentStandard));
}

function replaceInstance(instances: C1_EloItem_Data[], definitionId: string, instance?: C1_EloItem_Data) {
  const index = instances.findIndex((item) => item.definitionId === definitionId);
  if (index < 0) return instance ? [...instances, instance] : instances;
  if (!instance) return instances.filter((_, itemIndex) => itemIndex !== index);
  return instances.map((item, itemIndex) => (itemIndex === index ? instance : item));
}

function longestTagLength(instances: C1_EloItem_Data[]): number {
  return instances.reduce(
    (longest, instance) => Math.max(longest, instance.tag?.length ?? 0, longestTagLength(instance.items)),
    0,
  );
}

function updateRepeatedChild(
  definition: C1_EloItem_Definition,
  instance: C1_EloItem_Data,
  childDefinition: C1_EloItem_Definition,
  nextChild?: C1_EloItem_Data,
): C1_EloItem_Data {
  const repeatIndex = childDefinition.repeatIndex;
  if (repeatIndex === undefined) {
    return {
      ...instance,
      items: replaceInstance(instance.items, childDefinition.id, nextChild),
    };
  }

  const currentCount = repeatItemCount(definition, instance);
  if (nextChild) {
    return {
      ...instance,
      details: {
        ...instance.details,
        itemCount: Math.max(currentCount, repeatIndex),
      },
      items: replaceInstance(instance.items, childDefinition.id, nextChild),
    };
  }

  const currentChild = instance.items.find((item) => item.definitionId === childDefinition.id);
  return currentChild ? removeRepeatedItem(instance, instance.id, currentChild.id, definition) : instance;
}

function buttonFrontViewData(item: C1_Button) {
  if (item.owner?.isSensor) return undefined;
  const definition = item.definition?.toDefinition();
  if (definition && definition.type !== C1_EloItem_Type.Button) return undefined;
  const properties = item.owner?.baseProps.buttonBaseProps;
  if (!properties) return undefined;
  const uiTemplate = buttonUiTemplate(item.owner?.baseProps.buttonBaseProps?.type);
  return { properties, uiTemplate };
}

/** Opens the full device page independently of the navigator's active-plan routing. */
export function C1_ButtonAdvancedProperties({ button, iconOnly = false }: { button: Button; iconOnly?: boolean }) {
  if (state.settings.applicationMode !== ApplicationMode.Main) return null;
  return (
    <div className={iconOnly ? undefined : "C1_buttonEditorActions"} style={iconOnly ? { display: "contents" } : undefined}>
      <button type="button" className={iconOnly ? "dockIconButton" : undefined}
        aria-label={itx("c1.buttonEditor.advancedProperties", "Erweiterte Eigenschaften")}
        data-tooltip-id="tv-tooltip" data-tooltip-content={itx("c1.buttonEditor.advancedProperties", "Erweiterte Eigenschaften")}
        data-testid="c1-button-advanced-properties" onClick={(event) => {
        event.stopPropagation();
        button.configTemplate = false;
        button.treeViewModel.handleOpenId(button.id);
      }}>
        {iconOnly ? <Settings size={18} aria-hidden="true" /> : itx("c1.buttonEditor.advancedProperties", "Erweiterte Eigenschaften")}
      </button>
    </div>
  );
}

export function C1_ButtonEditorContent({ item, actionsInHeader = false, onPreviewContextChange, inlinePlan = false }: {
  item: C1_Button;
  actionsInHeader?: boolean;
  inlinePlan?: boolean;
  onPreviewContextChange?: (context: { itemId: string; page: number; press: number }) => void;
}) {
  useModel(c1_state);
  useOnChangeWatcher(state.selectionManager);
  useOnChangeWatcher(item.owner);
  useOnChangeWatcher(item.owner?.template);
  useOnChangeWatcher(item.owner?.channels);
  const view = item ? buttonFrontViewData(item) : undefined;
  const editorRef = useRef<HTMLDivElement>(null);
  const hasView = !!view;
  useLayoutEffect(() => {
    const panel = editorRef.current?.closest<HTMLElement>(".C1_dialog");
    if (panel) return freezeEditorSizeAfterLayout(panel);
  }, [item.id, hasView]);
  if (!item.owner) return null;
  const assignmentChannel = inlinePlan ? selectedButtonAssignmentChannel(item.owner, state.selectionManager.selectedObject) : undefined;

  return (
    <div ref={editorRef} className="C1_buttonFrontViewDialog">
      <C1_EloNameField obj={item.owner} />
      {item.owner && (
        // Fill the editor once sized, without contributing product labels to its intrinsic width.
        <div className="C1_buttonProductSelection">
          <Suspense fallback={null}><TemplateSelection device={item.owner} productType={item.owner.isSensor ? "Sensor" : "Button"} context="c1" /></Suspense>
        </div>
      )}
      {view ? <C1_ButtonEditorView item={item} actionsInHeader={actionsInHeader} onPreviewContextChange={onPreviewContextChange} inlinePlan={inlinePlan} /> : <C1_ButtonChannelList button={item.owner} />}
      {assignmentChannel && <p className="C1_mobileAssignmentHint" data-testid="c1-mobile-assignment-hint" role="status">
        {itx("c1.mobile.assignInPlan", "Klicke Verbraucher im Plan an um sie mit der Taste {{name}} zu verbinden.",
          {"{{name}}": assignmentChannel.getChannelNameForUI()})}
      </p>}
      {!actionsInHeader && <C1_ButtonAdvancedProperties button={item.owner} />}
    </div>
  );
}

/** Renders the interactive title shared by all button editor variants. */
export function C1_ButtonEditorHeading({ item }: { item: C1_Button }) {
  const button = item.owner as BaseModel | undefined;
  useOnChangeWatcher(button);
  const tag = button?.tag?.trim();
  const name = button?.displayName ?? "";
  const title = [tag, name].filter(Boolean).join(" · ");

  return (
    <span
      className="C1_buttonEditorHeading C1_buttonEditorHeading--compact"
      onClick={(event) => {
        event.stopPropagation();
        if (button) {
          c1_state.setSelection(item);
          c1_state.zoomNavigatorObject?.(button);
        }
      }}
    >
      <span className="C1_buttonEditorTitle" title={title}>
        {tag && <>
          <span className="C1_buttonEditorTitleTag">{tag}</span>
          <span className="C1_buttonEditorTitleSeparator">·</span>
        </>}
        <span className="C1_buttonEditorTitleText">{name}</span>
      </span>
    </span>
  );
}

export function C1_ButtonEditDialog() {
  useModel(c1_state);
  useOnChangeWatcher(state.dockManager);
  const panel = state.dockManager.buttonEditor;
  const item = panel.content.value;
  if (panel.visibility.value !== "visible" || !item) return null;

  return (
    <C1_Dialog
      open
      modal={false}
      showActions={false}
      title={<C1_ButtonEditorHeading item={item} />}
      className="C1_buttonFrontViewDialog"
      onCancel={() => state.dockManager.closeButtonEditor()}
    >
      <C1_ButtonEditorContent item={item} />
    </C1_Dialog>
  );
}

/** Gemeinsame Überschrift für freie und angedockte Leuchten-Editoren. */
export function C1_LightEditorHeading({ item }: { item: C1_Consumer }) {
  const group = consumerEditorSocketGroup(item);
  useOnChangeWatcher(group?.owner);
  const title = (group?.owner ?? item.owner)?.getDisplayName({usage: DisplayNameUsage.EditorTitle});
  return <span className="C1_buttonEditorHeading dockTitle">
    {/* Pinbutton des Leuchten-Editors vorläufig deaktiviert: <C1_EloEditorPin item={item} /> */}
    <span title={title}>{title}</span>
  </span>;
}

export function C1_LightEditorContent({ item }: { item: C1_Consumer }) {
  useModel(c1_state);
  useOnChangeWatcher(item.owner);
  useOnChangeWatcher(item.lightGroup?.owner);
  const socketGroup = consumerEditorSocketGroup(item);
  if (socketGroup) return <C1_SocketGroupEditor group={socketGroup} />;
  if (item.lightGroup) {
    return <>
      <C1_EloNameField obj={item.owner} />
      <C1_FurnitureEditor model={c1_state} furniture={item.lightGroup} showTitle={false} showEloEquipment={false} />
    </>;
  }
  if (!hasLightEditorContent(item)) return <>
    <C1_EloNameField obj={item.owner} />
    <Suspense fallback={null}><ConsumerTypeSelection consumer={item.consumer} /></Suspense>
  </>;
  const definition = item.definition!.toDefinition();

  return <C1_LightEditorFields
    name={item.owner?.name ?? ""}
    onNameChange={(name) => { if (item.owner) item.owner.name = name; }}
    channelType={item.channelType}
    isDali={item.isDali}
    channelTypeOptions={definition.details?.channelType?.options}
    onChannelTypeChange={(value) => item.setChannelType(value)}
    onDaliChange={(value) => item.setDali(value)}
    lightProductId={item.lightProductId}
    lightProductIds={getLightProductIdsForUsage(definition.usageCategory)}
    onLightProductChange={(value) => item.setLightProductId(value)}
  />;
}

/** Nur im Elo-Modus ersetzt die Steckdosengruppe den einzelnen Steckplatz im Editor. */
function consumerEditorSocketGroup(item: C1_Consumer): C1_EloGroup | undefined {
  const mode = c1_state.editMode.value;
  return mode === "EloConcept" || mode === "EloDetail" ? socketGroupForSelection(item) : undefined;
}

/** Dieselben Gruppen-/Steckplatzfelder wie im Ausstattungskontext, auf diese Gruppe begrenzt. */
export function C1_SocketGroupEditor({ group }: { group: C1_EloGroup }) {
  useOnChangeWatcher(group.owner);
  const definition = group.definition?.toDefinition();
  if (!definition) return null;
  const instance = group.toLegacyTreeData();
  return <div className="C1_EloEquipmentEditor C1_staticHeader">
    <C1_EloNameField obj={group.owner} />
    <div className="estada-editorOptionList">
      <EquipmentItem
        definition={{ ...definition, allways: true }}
        instance={instance}
        depth={0}
        showTags={c1_state.settings.showTags}
        tagEditable={c1_state.settings.tagEditable}
        tagColumnWidth={`calc(${Math.max(1, longestTagLength([instance]))}ch + 14px)`}
        equipmentStandard={c1_state.project.equipmentStandard}
        supportsDALI={c1_state.settings.supportsDALI}
        selectedItemId={c1_state.selection?.id}
        onChange={(next) => {
          if (!next) return;
          const context = eloRootAndOwner(group);
          if (!context) return;
          const root = updateEloItemData(context.root.toLegacyTreeData(), group.id, () => next);
          // Die Auswahl bleibt auf dem Steckplatz, auch nach Änderungen an der Gruppe.
          commitOwnerEloTree(context.owner, root, undefined, true, true);
        }}
      />
    </div>
  </div>;
}

export function C1_LightEditDialog() {
  useModel(c1_state);
  useOnChangeWatcher(state.dockManager);
  const panel = state.dockManager.lightEditor;
  const item = panel.content.value;
  if (panel.visibility.value !== "visible" || !item) return null;

  return (
    <C1_Dialog
      open
      modal={false}
      showActions={false}
      title={<C1_LightEditorHeading item={item} />}
      className="C1_lightEditDialog"
      onCancel={() => state.dockManager.closeLightEditor()}
    >
      <C1_LightEditorContent item={item} />
    </C1_Dialog>
  );
}

function DetailEditor({
  definition,
  instance,
  model,
  onChange,
  equipmentStandard,
}: {
  definition: C1_EloItem_Definition;
  instance: C1_EloItem_Data;
  model?: BaseModel;
  onChange: (instance: C1_EloItem_Data) => void;
  equipmentStandard: C1_EquipmentStandard;
  supportsDALI: boolean;
}): React.ReactElement | null {
  const language = c1_state.language;
  // Leuchtendetails werden im eigenen Verbraucher-Editor bearbeitet.
  if (definition.type === C1_EloItem_Type.Light || !definition.details) return null;

  const options: C1_EloItem_Detail_Options = definition.details ?? {};
  const details = { ...initialDetails(definition, equipmentStandard), ...instance.details };
  const setDetail = <K extends keyof C1_EloItem_Details>(key: K, value: C1_EloItem_Details[K]) => {
    onChange({
      ...instance,
      details: { ...details, [key]: value },
      properties:
        key === "buttonType"
          ? createDefaultButtonProperties(getButtonProductDefinition(value as string | undefined))
          : instance.properties,
    });
  };

  const detailEditors: React.ReactElement[] = [];

  detailEditors.push(
      ...(["plugType", "voltage"] as const).flatMap((key) => {
        const detailOption = options[key];
        if (!detailOption || detailOption.options.length < 2) return [];
        const valueOrOptions = detailOption.options;
        if (key === "plugType" && model?.c1 instanceof C1_Consumer) {
          return [<C1_SocketTypeSelect key={key} item={model.c1} options={valueOrOptions} />];
        }

        const optionLabel = (option: string) => (key === "plugType" ? C1_ELO_PLUG_TYPE_LABELS[option] : C1_ELO_VOLTAGE_LABELS[option]) ?? option;

        return [
          <select
            key={key}
            className="C1_EloDetailSelect dockControl"
            aria-label={key === "plugType" ? itx("c1.ui.Steckdosentyp", "Steckdosentyp") : itx("c1.ui.Spannung", "Spannung")}
            value={String(details[key] ?? "")}
            onChange={(event) => setDetail(key, event.target.value as C1_EloItem_Details[typeof key])}
          >
            <option value="" disabled>
              {itx("c1.ui.Kein_Standard_definiert", "Kein Standard definiert")} </option>
            {valueOrOptions.map((option) => (
              <option key={option} value={option}>
                {optionLabel(String(option))}
              </option>
            ))}
          </select>,
        ];
      }),
    );

  if (options.sensorTypes) {
    detailEditors.push(
      <div key="sensorTypes">
        {options.sensorTypes.options.map((sensorType) => {
          const selected = details.sensorTypes ?? [];
          return (
            <label key={sensorType} style={{ display: "block", marginLeft: 8 }}>
              <input
                type="checkbox"
                className="C1_checkbox"
                checked={selected.includes(sensorType)}
                onChange={(event) =>
                  setDetail(
                    "sensorTypes",
                    event.target.checked ? [...selected, sensorType] : selected.filter((item) => item !== sensorType),
                  )
                }
              />{" "}
              {C1_ELO_SENSOR_TYPE_LABELS[sensorType]}
            </label>
          );
        })}
      </div>,
    );
  }

  if (detailEditors.length === 0) return null;
  if (detailEditors.length > 1) {
    return <div className="C1_EloDetailEditors">{detailEditors}</div>;
  }

  return detailEditors[0];
}

function EquipmentItem({
  definition,
  instance,
  onChange,
  depth,
  showTags,
  tagEditable,
  tagColumnWidth,
  equipmentStandard,
  supportsDALI,
  selectedItemId,
  repeatEditable = false,
}: {
  definition: C1_EloItem_Definition;
  instance?: C1_EloItem_Data;
  onChange: (instance?: C1_EloItem_Data) => void;
  depth: number;
  showTags: boolean;
  tagEditable: boolean;
  tagColumnWidth: string;
  equipmentStandard: C1_EquipmentStandard;
  supportsDALI: boolean;
  selectedItemId?: string;
  repeatEditable?: boolean;
}) {
  const language = c1_state.language;
  // The furniture owns this container; only its electrical children are selectable.
  if (definition.requiresFurnitureProperty && !instance) {
    instance = { id: newId(), definitionId: definition.id, items: [] };
  }
  const active = Boolean(instance);
  const model = instance ? state.objectRegistry.getObject<BaseModel>(instance.id) : undefined;
  useOnChangeWatcher(model);
  const icon = getC1EloEquipmentIcon(
    definition,
    instance?.details ?? initialDetails(definition, equipmentStandard),
    model,
  );

  const onDetailChange = (nextInstance: C1_EloItem_Data) => {
    const itemDefinitions = expandedItemDefinitions(definition, nextInstance.details);
    const items = itemDefinitions.flatMap((itemDefinition) => {
      const existingItem = nextInstance.items.find((item) => item.definitionId === itemDefinition.id);
      return existingItem
        ? [existingItem]
        : isEloItemDefaultEnabled(itemDefinition, equipmentStandard)
          ? [createInstance(itemDefinition, equipmentStandard)]
          : [];
    });
    onChange({ ...nextInstance, items });
  };
  const detailEditor = instance && (
    <DetailEditor
      definition={definition}
      instance={instance}
      model={model}
      onChange={onDetailChange}
      equipmentStandard={equipmentStandard}
      supportsDALI={supportsDALI}
    />
  );
  const variableRepeatGroup = Array.isArray(definition.details?.repeatCount);
  const itemDefinitions = instance
    ? expandedItemDefinitions(
        definition,
        variableRepeatGroup
          ? {
              ...instance.details,
              itemCount: Math.min(repeatItemLimit(definition), repeatItemCount(definition, instance) + 1),
            }
          : instance.details,
      )
    : [];

  const style: React.CSSProperties = {
    marginLeft: depth * 16,
    paddingLeft: depth ? 10 : 0,
  };
  const hasToggle = !definition.requiresFurnitureProperty && !definition.allways && (definition.repeatIndex === undefined || repeatEditable);
  const itemSelected = selectedItemId !== undefined && instance?.id === selectedItemId;

  return (
    <div style={style}>
      <div
        className={`C1_EloEquipmentItemRow${hasToggle ? " C1_EloEquipmentItemRow--hasToggle" : ""}${itemSelected ? " C1_selected" : ""}`}
        data-c1-elo-item-id={instance?.id}
        style={{
          display: "flex",
          flexWrap: definition.repeatIndex === undefined ? "wrap" : "nowrap",
          width: "100%",
          minWidth: 0,
          gap: 8,
          alignItems: "center",
        }}
      >
        <label
          className={`estada-editorCheckbox${definition.repeatIndex !== undefined && repeatEditable && !active ? " C1_repeatAddRow" : ""}`}
        >
          {definition.allways || definition.requiresFurnitureProperty ? null : definition.repeatIndex !== undefined && repeatEditable && !active ? (
            <button
              type="button"
              className="C1_repeatAddButton"
              title={itx("c1.ui.value1_hinzufugen", "{{value1}} hinzufügen", { "{{value1}}": String(definition.description[language]) })}
              aria-label={itx("c1.ui.value1_hinzufugen", "{{value1}} hinzufügen", { "{{value1}}": String(definition.description[language]) })}
              onClick={() => onChange(createInstance(definition, equipmentStandard))}
            >
              <span className="C1_repeatAddIcon" aria-hidden="true" />
            </button>
          ) : definition.repeatIndex === undefined || repeatEditable ? (
            <input
              type="checkbox"
              className="C1_checkbox"
              checked={active}
              onChange={(event) =>
                onChange(event.target.checked ? createInstance(definition, equipmentStandard) : undefined)
              }
            />
          ) : null}
          {icon && <span className="C1_EloEquipmentIcon" aria-hidden="true">{icon}</span>}
          {showTags && definition.hasTag !== false && (
            <span className="C1_EloEquipmentTagSlot" style={{ width: tagColumnWidth }}>
              {instance &&
                (tagEditable ? (
                  <input
                    type="text"
                    className="C1_EloEquipmentTag dockControl"
                    aria-label={itx("c1.ui.Tag_fur_value1", "Tag für {{value1}}", { "{{value1}}": String(definition.description[language]) })}
                    title={itx("ColumnFilterUI.tag", "Tag")}
                    value={instance.tag ?? ""}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => onChange({ ...instance, tag: event.target.value || undefined })}
                  />
                ) : (
                  <span className="C1_EloEquipmentTag C1_EloEquipmentTag--value">{instance.tag || "—"}</span>
                ))}
            </span>
          )}
          <span className="C1_EloEquipmentItemName">{model?.displayName ?? (instance?.name?.trim() || definition.description[language])}</span>
        </label>

        {active && model && (
          <button type="button" className="C1_EloEquipmentNavigate dockIconButton"
            aria-label={itx("treeMenuIcon.navigateToObject", "Objekt auswählen und heranzoomen")}
            title={itx("treeMenuIcon.navigateToObject", "Objekt auswählen und heranzoomen")}
            data-testid="c1-elo-item-navigate"
            onClick={() => {
              state.selectionManager.selectedObject = model;
              c1_state.zoomNavigatorObject?.(model);
            }}>
            <i className="las la-external-link-alt" aria-hidden="true" />
          </button>
        )}
        {detailEditor}
      </div>

      {instance && (
        <>
          {itemDefinitions.map((childDefinition) => {
            const childInstance = instance.items.find((item) => item.definitionId === childDefinition.id);
            return (
              <EquipmentItem
                key={childDefinition.id}
                definition={childDefinition}
                instance={childInstance}
                depth={depth + 1}
                showTags={showTags}
                tagEditable={tagEditable}
                tagColumnWidth={tagColumnWidth}
                equipmentStandard={equipmentStandard}
                supportsDALI={supportsDALI}
                selectedItemId={selectedItemId}
                repeatEditable={variableRepeatGroup}
                onChange={(nextChild) =>
                  onChange(
                    variableRepeatGroup
                      ? updateRepeatedChild(definition, instance, childDefinition, nextChild)
                      : {
                          ...instance,
                          items: replaceInstance(instance.items, childDefinition.id, nextChild),
                        },
                  )
                }
              />
            );
          })}
        </>
      )}
    </div>
  );
}

/** Selection is presentation only and must belong to this editor's equipment tree. */
export function selectedEquipmentItemId(tree: C1_EloItem_Data | undefined, requestedId: string | undefined): string | undefined {
  if (!tree || !requestedId) return undefined;
  const contains = (item: C1_EloItem_Data): boolean => item.id === requestedId || item.items.some(contains);
  return tree.items.some(contains) ? requestedId : undefined;
}

/** Edits only the supplied tree through onChange; device names belong to their dedicated editors. */
export default function C1_EloEquipmentEditor({
  state,
  rootDefinitionId,
  eloTree,
  onChange,
  eloDefinition,
  collapsible = true,
  showHeader = true,
  selectedItemId: requestedSelectionId,
}: {
  state: C1_State;
  rootDefinitionId: string;
  eloTree?: C1_EloItem_Data;
  onChange: (instance: C1_EloItem_Data) => void;
  eloDefinition?: C1_EloItem_Definition[];
  collapsible?: boolean;
  showHeader?: boolean;
  selectedItemId?: string;
}) {
  useOnChangeWatcher(state.settings);
  const editorRef = useRef<HTMLDivElement>(null);
  const selectedItemId = selectedEquipmentItemId(eloTree, requestedSelectionId);
  useEffect(() => {
    if (!selectedItemId) return;
    const selectedRow = Array.from(
      editorRef.current?.querySelectorAll<HTMLElement>("[data-c1-elo-item-id]") ?? [],
    ).find((row) => row.dataset.c1EloItemId === selectedItemId);
    selectedRow?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedItemId]);

  useLayoutEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    // Zuerst die natürliche, vom jeweiligen Inhalt bestimmte Breite messen.
    // Anschließend erhalten alle sichtbaren Detailauswahlen den größten Wert.
    editor.style.removeProperty("--c1-elo-detail-select-width");
    const detailSelects = Array.from(editor.querySelectorAll<HTMLElement>(".C1_EloDetailSelect"));
    if (detailSelects.length === 0) return;

    const widestSelect = Math.max(...detailSelects.map((select) => select.getBoundingClientRect().width));
    editor.style.setProperty("--c1-elo-detail-select-width", `${Math.ceil(widestSelect)}px`);
  });

  useEffect(() => {
    if (!eloDefinition?.length) return;
    const hasMatchingRoot = eloTree?.definitionId === rootDefinitionId;
    const hasMissingAllwaysItem = eloDefinition.some(
      (definition) => definition.allways && !eloTree?.items.some((item) => item.definitionId === definition.id),
    );
    if (hasMatchingRoot && !hasMissingAllwaysItem) return;

    onChange(
      reconcileEloEquipmentInstance(
        rootDefinitionId,
        eloDefinition,
        state.project.equipmentStandard,
        eloTree,
        state.settings.buttonDefaults,
      ),
    );
  }, [eloDefinition, eloTree, onChange, rootDefinitionId, state.project.equipmentStandard]);

  if (!eloDefinition?.length) return null;

  const eloItemsVisible = collapsible ? state.eloItemsVisible.value : true;
  const language = state.language;
  const showTags = state.settings.showTags;
  const tagEditable = state.settings.tagEditable;
  const equipmentStandard = state.project.equipmentStandard;
  const supportsDALI = state.settings.supportsDALI;
  const selectedItems =
    eloTree?.definitionId === rootDefinitionId
      ? eloTree.items
      : createDefaultInstances(eloDefinition, equipmentStandard);
  const tagColumnWidth = `calc(${Math.max(1, longestTagLength(selectedItems))}ch + 14px)`;

  const updateSelectedItem = (definitionId: string, instance?: C1_EloItem_Data) => {
    const wasEnabled = selectedItems.some((item) => item.definitionId === definitionId);
    const isEnabled = instance !== undefined;
    if (wasEnabled !== isEnabled) {
      state.eventLogger.addEvent(C1_EventKey.EloItemToggled, { definitionId, enabled: isEnabled });
      if (!isEnabled) state.eventLogger.addEvent(C1_EventKey.EloItemDeleted, { definitionId });
    }
    const items = replaceInstance(selectedItems, definitionId, instance);
    onChange({
      id: eloTree?.id ?? newId(),
      tag: eloTree?.tag,
      definitionId: rootDefinitionId,
      items,
    });
  };

  return (
    <div ref={editorRef} className={`C1_EloEquipmentEditor ${collapsible ? "" : "C1_staticHeader"}`}>
      {showHeader && (
        <h1 className="dockTitle">
          {collapsible ? (
            <button
              type="button"
              className="C1_EloEquipmentEditorToggle dockDisclosure"
              aria-label={eloItemsVisible ? itx("c1.ui.Elektro_Installation_einklappen", "Elektro Installation einklappen") : itx("c1.ui.Elektro_Installation_ausklappen", "Elektro Installation ausklappen")}
              aria-expanded={eloItemsVisible}
              data-expanded={eloItemsVisible}
              onClick={() => {
                state.eloItemsVisible.value = !eloItemsVisible;
              }}
            >
              <span>{itx("c1.ui.Elektro_Installation", "Elektro Installation")}</span>
              <span className="C1_EloEquipmentEditorToggleArrow" aria-hidden="true">
                {eloItemsVisible ? "▴" : "▾"}
              </span>
            </button>
          ) : (
            <span>{itx("c1.ui.Elektro_Installation", "Elektro Installation")}</span>
          )}
        </h1>
      )}
      {eloItemsVisible && (
        <div className="estada-editorOptionList">
          {eloDefinition.map((definition) => (
            <EquipmentItem
              key={definition.id}
              definition={definition}
              instance={selectedItems.find((item) => item.definitionId === definition.id)}
              depth={0}
              showTags={showTags}
              tagEditable={tagEditable}
              tagColumnWidth={tagColumnWidth}
              equipmentStandard={equipmentStandard}
              supportsDALI={supportsDALI}
              selectedItemId={selectedItemId}
              onChange={(instance) => updateSelectedItem(definition.id, instance)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
